import React from "react";
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { useDispatch } from "react-redux";
import { logout } from "../../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { useUser } from '../../contexts/UserContext';

const Sidebar = ({
  isOpen,
  toggleSidebar,
  setShowAnalytics,
  setShowCandidateStats,
  handleAnalytics,
}) => {
  const { role } = useUser();
  const [list, setList] = React.useState("home");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleClick = (id) => {
    switch (id) {
      case "home":
        navigate("/dashboard");
        handleAnalytics(false);
        break;
      case "operator":
        navigate("operators");
        handleAnalytics(false);
        break;
      case "analytics":
        navigate("/dashboard");
        handleAnalytics(true);
        break;
      case "CsrReport":
        navigate("CsrReport");
        break;
      case "TableStatic":
        navigate("TableStatic");
        break;
      case "settings":
        navigate("/dashboard/settings");
        break;
      default:
        break;
    }
  };

  const baseItems = [
    {
      id: "home",
      icon: <HomeIcon htmlColor="#605d5d" />,
      iconDark: <HomeIcon htmlColor="#fff" />,
      text: "Dashboard",
    },
    {
      id: "operator",
      icon: <SettingsIcon htmlColor="#605d5d" />,
      iconDark: <SettingsIcon htmlColor="#fff" />,
      text: "Operator",
    },
  ];

  const adminItems = [
    {
      id: "analytics",
      icon: <BarChartIcon htmlColor="#605d5d" />,
      iconDark: <BarChartIcon htmlColor="#fff" />,
      text: "Analytics",
    },
    {
      id: "CsrReport",
      icon: <AssignmentLateIcon htmlColor="#605d5d" />,
      iconDark: <AssignmentLateIcon htmlColor="#fff" />,
      text: "CSR Report",
    },
    {
      id: "TableStatic",
      icon: <EqualizerIcon htmlColor="#605d5d" />,
      iconDark: <EqualizerIcon htmlColor="#fff" />,
      text: "TableStatic",
    },
    {
      id: "settings",
      icon: <SettingsIcon htmlColor="#605d5d" />,
      iconDark: <SettingsIcon htmlColor="#fff" />,
      text: "Settings",
    },
  ];

  const logoutItem = {
    id: "logout",
    icon: <LogoutIcon htmlColor="#605d5d" />,
    iconDark: <LogoutIcon htmlColor="#fff" />,
    text: "Logout",
  };

  const sideBarItems = [...baseItems, ...(role === "admin" ? adminItems : []), logoutItem];

  return (
    <Drawer
      variant="permanent"
      open={true}
      sx={{
        "& .MuiDrawer-paper": {
          width: isOpen ? 240 : 60,
          transition: "width 0.3s ease",
          overflowX: "hidden",
          padding: 2,
          backgroundColor: "#ffffff",
          display: "flex", // Enable flex on the drawer paper
          flexDirection: "column",
        },
      }}
    >
      {/* 1. Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isOpen ? "space-between" : "center",
          padding: "8px",
          minHeight: "64px",
        }}
      >
        {isOpen && (
          <Typography sx={{ lineHeight: 0 }} color="inherit">
            <img src="/assets/icons/logo.jpeg" width={58} height={75} alt="Logo" />
          </Typography>
        )}
        <IconButton color="inherit" onClick={toggleSidebar}>
          <MenuIcon />
        </IconButton>
      </Box>

      {/* 2. Main Navigation Links (Flexible space) */}
      <List
        sx={{
          flexGrow: 1, // This pushes the items below it to the bottom
          paddingTop: 4,
          backgroundColor: "#ffffff",
        }}
      >
        {sideBarItems
          .filter((item) => item.id !== "logout") // Filter out logout to place it specifically
          .map((item, index) => (
            <div
              key={index}
              onMouseOver={() => setList(item.id)}
              onMouseLeave={() => setList("")}
            >
              <ListItem
                button
                sx={{
                  ":hover": {
                    backgroundColor: "#1976d2",
                    color: "white",
                  },
                  backgroundColor: list === item.id ? "#1976d2" : "",
                  color: list === item.id ? "white" : "",
                  borderRadius: 4,
                  cursor: "pointer",
                  marginBottom: 2,
                }}
                onClick={() => handleClick(item.id)}
              >
                <ListItemIcon sx={{ color: "#dadada", minWidth: 42 }}>
                  {list === item.id ? item.iconDark : item.icon}
                </ListItemIcon>
                {isOpen && <ListItemText primary={item.text} />}
              </ListItem>
            </div>
          ))}
      </List>

      {/* 3. Bottom Section (Logout + Copyright) */}
      <Box sx={{ mt: "auto", pb: 1 }}>
        <ListItem
          button
          onMouseOver={() => setList("logout")}
          onMouseLeave={() => setList("")}
          sx={{
            ":hover": {
              backgroundColor: "#de0202",
              color: "white",
            },
            backgroundColor: list === "logout" ? "#de0202" : "",
            color: list === "logout" ? "white" : "",
            borderRadius: 4,
            cursor: "pointer",
            marginBottom: 2,
          }}
          onClick={handleLogout}
        >
          <ListItemIcon sx={{ color: "#dadada", minWidth: 42 }}>
            {list === "logout" ? logoutItem.iconDark : logoutItem.icon}
          </ListItemIcon>
          {isOpen && <ListItemText primary={logoutItem.text} />}
        </ListItem>

        {isOpen && (
          <Box sx={{ textAlign: "center", px: 1, mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "#9e9e9e", fontSize: "0.6rem", display: "block", lineHeight: 1.2 }}
            >
              Website Content Managed by Abhinnovation Technologies Pvt. Ltd.
              Designed, Developed and Hosted by Abhinnovation Technologies Pvt. Ltd.
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#9e9e9e", fontSize: "0.6rem", fontWeight: "bold" }}
            >
              © All rights reserved
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;